/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { deactivateScreenSharing, deviceRequestAccepted, deviceRequestFailed, devicesFound, errorOccurred, errorsCleared, mergedStreamUpdated, screenSharingEnded, setScreenSharingType, setSystemPermissionError } from '../../actions';
import { AUDIO_DEVICE_CHANGED, AUDIO_DEVICE_DISABLED, CLEAN_UP, INITIALIZE_WEBCAM, SCREEN_SHARING_DISABLED, SCREEN_SHARING_ENABLED, SCREEN_SHARING_ENDED, START_MERGED_STREAM, STOP_MERGED_STREAM, UNMOUNT_CLEAN_UP, VIDEO_DEVICE_CHANGED, VIDEO_DEVICE_DISABLED } from '../../constants/ActionTypes';
import { DeviceError } from '../../constants/DeviceError';
import { translate } from '../../constants/translated/translations';
import { enumerateDevices } from '../../core/mediaDevices';
import { determineScreenSharingType } from '../../utils';
import StreamManager, { StreamDeviceTypes } from './StreamManager';
import { isDeviceNotFoundError, isVideoNotReadableError, isPermissionDeniedBySystemError, isPermissionDeniedError, mapStreamsForMerger } from './helpers';
function processErrors(errors, {
  dispatch
}) {
  Object.keys(errors).forEach(device => {
    const error = errors[device];
    if (error) {
      switch (true) {
        case isVideoNotReadableError(error):
        case isDeviceNotFoundError(error):
        case isPermissionDeniedBySystemError(error):
          dispatch(setSystemPermissionError(device));
          return;
        case isPermissionDeniedError(error):
          dispatch(deviceRequestFailed());
          return;
        default:
          dispatch(errorOccurred(translate('DEFAULT_ERROR')));
          return;
      }
    }
  });
}
async function initializeWebcam({
  dispatch,
  state,
  streamManager,
  getState
}) {
  // initiate request for device permissions
  // we will get real stream after that
  const deviceTestStreams = await streamManager.getStreams({
    [StreamDeviceTypes.Webcam]: {
      video: {
        deviceId: state.videoDeviceId
      },
      audio: {
        deviceId: state.audioDeviceId
      }
    }
  });

  // as we made previous getStreams request only to get permissions
  // we just immediately stop obtained tracks
  await streamManager.removeAllStreams();
  const deviceTypes = await enumerateDevices();
  if (deviceTestStreams.errors[DeviceError.Mic] && isPermissionDeniedBySystemError(deviceTestStreams.errors[DeviceError.Mic])) {
    // if mic is blocked on the system level we can't use any at all
    deviceTypes.audioinput = isPermissionDeniedBySystemError(deviceTestStreams.errors[DeviceError.Mic]) ? [] : deviceTypes.audioinput;
    dispatch(setSystemPermissionError(DeviceError.Mic));
  }
  if (deviceTestStreams.errors[DeviceError.Webcam] && isPermissionDeniedBySystemError(deviceTestStreams.errors[DeviceError.Webcam])) {
    // if webcam is blocked on the system level we can't use any at all
    deviceTypes.videoinput = isPermissionDeniedBySystemError(deviceTestStreams.errors[DeviceError.Webcam]) ? [] : deviceTypes.videoinput;
    dispatch(setSystemPermissionError(DeviceError.Webcam));
  }
  dispatch(devicesFound(deviceTypes, {
    videoDeviceId: state.videoDeviceId,
    audioDeviceId: state.audioDeviceId
  }));

  // in devicesFound action we select actual device id to be initiated
  // but we need to take it from an updated redux state
  const updatedState = getState();
  const streams = await streamManager.getStreams({
    [StreamDeviceTypes.Webcam]: {
      video: {
        deviceId: updatedState.videoDeviceId
      },
      audio: {
        deviceId: updatedState.audioDeviceId
      }
    }
  });
  const streamsToMerge = mapStreamsForMerger(streams, {
    [StreamDeviceTypes.Webcam]: {
      audio: {
        muted: typeof updatedState.audioDeviceId !== 'string' ? updatedState.audioDeviceId : false
      }
    },
    [StreamDeviceTypes.ScreenSharing]: {}
  });

  // to eliminate situation when stream is visible but we still have spinner
  // we merge actual stream after devices are ready
  await updatedState.streamMerger.mergeStreams(streamsToMerge);
  dispatch(mergedStreamUpdated());
  dispatch(deviceRequestAccepted());
  processErrors(streams.errors, {
    dispatch
  });
}
async function changeVideoDevice({
  state,
  streamManager,
  action,
  dispatch
}) {
  await streamManager.removeWebcamStream();
  const config = {
    [StreamDeviceTypes.Webcam]: {
      video: {
        deviceId: action.id || state.lastVideoDeviceId
      },
      audio: {
        deviceId: state.audioDeviceId
      }
    }
  };
  const streams = await streamManager.getStreams(config);
  const streamsToMerge = mapStreamsForMerger(streams, {
    [StreamDeviceTypes.Webcam]: {
      audio: {
        muted: !state.audioDeviceId
      }
    },
    [StreamDeviceTypes.ScreenSharing]: {}
  });
  await state.streamMerger.mergeStreams(streamsToMerge);
  dispatch(mergedStreamUpdated());
  processErrors(streams.errors, {
    dispatch
  });
}
async function changeAudioDevice({
  state,
  streamManager,
  action,
  dispatch
}) {
  await streamManager.removeWebcamStream();
  const config = {
    [StreamDeviceTypes.Webcam]: {
      video: {
        deviceId: state.videoDeviceId
      },
      audio: {
        deviceId: action.id
      }
    }
  };
  const streams = await streamManager.getStreams(config);
  const streamsToMerge = mapStreamsForMerger(streams, {
    [StreamDeviceTypes.Webcam]: {
      audio: {
        muted: !action.id
      }
    },
    [StreamDeviceTypes.ScreenSharing]: {}
  });
  await state.streamMerger.mergeStreams(streamsToMerge);
  dispatch(mergedStreamUpdated());
  processErrors(streams.errors, {
    dispatch
  });
}
async function disableVideoDevice({
  state,
  streamManager,
  dispatch
}) {
  await streamManager.removeWebcamStream();
  const config = {
    [StreamDeviceTypes.Webcam]: {
      video: {
        deviceId: null
      },
      audio: {
        deviceId: state.audioDeviceId
      }
    }
  };
  const streams = await streamManager.getStreams(config);
  const streamsToMerge = mapStreamsForMerger(streams, {
    [StreamDeviceTypes.Webcam]: {
      audio: {
        muted: !state.audioDeviceId
      }
    },
    [StreamDeviceTypes.ScreenSharing]: {}
  });
  await state.streamMerger.mergeStreams(streamsToMerge);
  dispatch(mergedStreamUpdated());
  processErrors(streams.errors, {
    dispatch
  });
}
async function disableAudioDevice({
  state,
  streamManager,
  dispatch
}) {
  await streamManager.removeWebcamStream();
  const config = {
    [StreamDeviceTypes.Webcam]: {
      video: {
        deviceId: state.videoDeviceId
      },
      audio: {
        deviceId: null
      }
    }
  };
  const streams = await streamManager.getStreams(config);
  const streamsToMerge = mapStreamsForMerger(streams, {
    [StreamDeviceTypes.Webcam]: {
      audio: {
        muted: true
      },
      [StreamDeviceTypes.ScreenSharing]: {}
    }
  });
  await state.streamMerger.mergeStreams(streamsToMerge);
  dispatch(mergedStreamUpdated());
  processErrors(streams.errors, {
    dispatch
  });
}
async function enableScreenSharing({
  dispatch,
  state,
  streamManager
}) {
  await streamManager.removeAllStreams();
  dispatch(errorsCleared());
  const config = {
    [StreamDeviceTypes.ScreenSharing]: {
      experimentalOptions: state.experimentalScreenShareOptions
    }
  };
  const streams = await streamManager.getStreams(config);
  if (!streams[StreamDeviceTypes.ScreenSharing]) {
    dispatch(deactivateScreenSharing());
  } else {
    const screenSharingType = determineScreenSharingType(streams[StreamDeviceTypes.ScreenSharing]);
    dispatch(setScreenSharingType(screenSharingType));
    const screenSharingTrack = streams[StreamDeviceTypes.ScreenSharing].getVideoTracks()[0];
    screenSharingTrack.onended = () => dispatch(screenSharingEnded());
  }
  const streamsToMerge = mapStreamsForMerger(streams, {
    [StreamDeviceTypes.Webcam]: {
      audio: {
        muted: !state.audioDeviceId
      }
    },
    [StreamDeviceTypes.ScreenSharing]: {}
  });
  state.streamMerger.mergeStreams(streamsToMerge);
  dispatch(mergedStreamUpdated());
  const screenSharingError = streams.errors[DeviceError.ScreenSharing];
  if (screenSharingError && (isPermissionDeniedError(screenSharingError) || isPermissionDeniedBySystemError(screenSharingError))) {
    dispatch(deactivateScreenSharing());
    dispatch(errorOccurred(translate('ERROR_SCREEN_SHARING_SYSTEM_PERMISSION'), false));

    // as we process ScreenSharing error differently we don't want default handlers to be applied
    streams.errors[DeviceError.ScreenSharing] = void 0;
  }
  processErrors(streams.errors, {
    dispatch
  });
}
async function endScreenSharing({
  state,
  streamManager,
  dispatch
}) {
  await streamManager.removeAllStreams();
  const config = {};
  if (state.videoDeviceId || state.audioDeviceId) {
    config[StreamDeviceTypes.Webcam] = {
      video: {
        deviceId: state.videoDeviceId
      },
      audio: {
        deviceId: state.audioDeviceId
      }
    };
  }
  const streams = await streamManager.getStreams(config);
  const streamsToMerge = mapStreamsForMerger(streams, {
    [StreamDeviceTypes.Webcam]: {
      audio: {
        muted: !state.audioDeviceId
      }
    }
  });
  await state.streamMerger.mergeStreams(streamsToMerge);
  dispatch(mergedStreamUpdated());
  processErrors(streams.errors, {
    dispatch
  });
}
export default function devicesMiddleware({
  streamConfigOverrides
}) {
  return ({
    dispatch,
    getState
  }) => {
    const streamManager = new StreamManager(streamConfigOverrides);
    return next => action => {
      async function runSideEffects() {
        const state = getState();
        const actionParams = {
          state,
          dispatch,
          streamManager,
          action,
          getState
        };
        switch (action.type) {
          case INITIALIZE_WEBCAM:
            return await initializeWebcam(actionParams);
          case VIDEO_DEVICE_CHANGED:
            return await changeVideoDevice(actionParams);
          case AUDIO_DEVICE_CHANGED:
            return await changeAudioDevice(actionParams);
          case VIDEO_DEVICE_DISABLED:
            return await disableVideoDevice(actionParams);
          case AUDIO_DEVICE_DISABLED:
            return await disableAudioDevice(actionParams);
          case SCREEN_SHARING_ENABLED:
            return await enableScreenSharing(actionParams);
          case SCREEN_SHARING_DISABLED:
          case SCREEN_SHARING_ENDED:
            return await endScreenSharing(actionParams);
          case START_MERGED_STREAM:
            state.streamMerger.start();
            return;
          case STOP_MERGED_STREAM:
            state.streamMerger.stop();
            return;
          case CLEAN_UP:
          case UNMOUNT_CLEAN_UP:
            state.streamMerger.stop();
            await streamManager.removeAllStreams();
            return;
          default:
            return;
        }
      }
      runSideEffects().catch(error => {
        console.error('Unprocessed error occurred in devicesMiddleware', error.name, error.message);
        dispatch(errorOccurred(translate('DEFAULT_ERROR')));
      });
      return next(action);
    };
  };
}